import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import tenantRequestService from '../../services/tenantRequestService';
import emailService from '../../services/emailService';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  InputAdornment,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Grid,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';

// Styled components for enhanced visuals
const GradientPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  position: 'relative',
}));
const GradientDivider = styled(Box)(({ theme }) => ({
  height: 8,
  width: '100%',
  background: 'linear-gradient(90deg, #0062cc 0%, #0073e6 50%, #2196f3 100%)',
  backgroundSize: '200% auto',
  animation: 'gradient 8s linear infinite',
  '@keyframes gradient': {
    '0%': { backgroundPosition: '0% center' },
    '100%': { backgroundPosition: '200% center' }
  }
}));
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 64,
  height: 64,
  margin: '0 auto 16px',
  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
}));
const StyledButton = styled(Button)(({ theme }) => ({
  padding: '12px 16px',
  borderRadius: theme.spacing(1.5),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(90deg, #0062cc 0%, #0073e6 50%, #2196f3 100%)',
  backgroundSize: '200% auto',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    animation: 'shine 1.5s infinite',
  },
  '@keyframes shine': {
    '100%': { left: '200%' }
  }
}));
const SubdomainInput = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden'
}));
// Background image rotation
const backgroundImages = [
  '/images/enterprise-bg-1.jpg',
  '/images/enterprise-bg-2.jpg',
  '/images/enterprise-bg-3.jpg',
];
const RegisterTenantPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Weak',
    color: 'error'
  });

  const countries = [
    { code: 'US', name: 'United States', phoneCode: '+1' },
    { code: 'CA', name: 'Canada', phoneCode: '+1' },
    { code: 'GB', name: 'United Kingdom', phoneCode: '+44' },
    { code: 'AU', name: 'Australia', phoneCode: '+61' },
    { code: 'DE', name: 'Germany', phoneCode: '+49' },
    { code: 'FR', name: 'France', phoneCode: '+33' },
    { code: 'JP', name: 'Japan', phoneCode: '+81' },
    { code: 'CN', name: 'China', phoneCode: '+86' },
    { code: 'IN', name: 'India', phoneCode: '+91' },
    { code: 'BR', name: 'Brazil', phoneCode: '+55' },
    { code: 'NG', name: 'Nigeria', phoneCode: '+234' },
    { code: 'ZA', name: 'South Africa', phoneCode: '+27' },
  ];

  const industries = [
    'Healthcare',
    'Finance & Banking',
    'Education',
    'Technology',
    'Manufacturing',
    'Retail & E-commerce',
    'Government',
    'Non-profit',
    'Professional Services',
    'Energy & Utilities',
    'Transportation & Logistics',
    'Media & Entertainment',
    'Telecommunications',
    'Construction & Real Estate',
    'Hospitality & Tourism',
    'Agriculture',
    'Pharmaceutical',
    'Insurance',
    'Legal Services',
    'Automotive',
    'Other'
  ];

  const companySizes = [
    'Startup (1-10 employees)',
    'Small (11-50 employees)',
    'Medium (51-200 employees)',
    'Large (201-500 employees)',
    'Enterprise (501-1,000 employees)',
    'Large Enterprise (1,001-5,000 employees)',
    'Global Enterprise (5,000+ employees)'
  ];

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      subdomain: '',
      ownerFirstName: '',
      ownerLastName: '',
      ownerEmail: '',
      ownerPassword: '',
      confirmPassword: '',
      phoneCountryCode: '+1',
      phoneAreaCode: '',
      phoneNumber: '',
      country: 'US',
      state: '',
      city: '',
      industry: '',
      companySize: ''
    }
  });

  const password = watch('ownerPassword');
  const subdomain = watch('subdomain');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkSubdomainAvailability = async () => {
      if (!subdomain || subdomain.length < 3) {
        setSubdomainAvailable(null);
        return;
      }
      try {
        setCheckingSubdomain(true);
        const response = await tenantRequestService.checkSubdomainAvailability(subdomain);
        setSubdomainAvailable(response.available);
      } catch (error) {
        console.error('Error checking subdomain:', error);
        setSubdomainAvailable(false);
      } finally {
        setCheckingSubdomain(false);
      }
    };
    const timer = setTimeout(() => {
      if (subdomain && subdomain.length >= 3) {
        checkSubdomainAvailability();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [subdomain]);

  useEffect(() => {
    const calculatePasswordStrength = (password) => {
      if (!password) {
        return { score: 0, label: 'Weak', color: 'error' };
      }
      let score = 0;
      if (password.length >= 8) score += 1;
      if (password.length >= 12) score += 1;
      if (/[A-Z]/.test(password)) score += 1;
      if (/[a-z]/.test(password)) score += 1;
      if (/[0-9]/.test(password)) score += 1;
      if (/[^A-Za-z0-9]/.test(password)) score += 1;
      let label = 'Weak';
      let color = 'error';
      if (score >= 5) {
        label = 'Strong';
        color = 'success';
      } else if (score >= 3) {
        label = 'Medium';
        color = 'warning';
      }
      return { 
        score: (score / 6) * 100, 
        label, 
        color 
      };
    };
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  const handleSubdomainChange = (e) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');
    setValue('subdomain', value);
  };

  const handleNext = async () => {
    let fieldsToValidate;
    switch(activeStep) {
      case 0:
        fieldsToValidate = ['name', 'subdomain', 'phoneCountryCode', 'phoneAreaCode', 'phoneNumber', 'country', 'city', 'state'];
        break;
      case 1:
        fieldsToValidate = ['ownerFirstName', 'ownerLastName', 'ownerEmail', 'ownerPassword', 'confirmPassword'];
        break;
      case 2:
        fieldsToValidate = ['industry', 'companySize'];
        break;
      default:
        fieldsToValidate = [];
    }
    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      
      if (subdomainAvailable === false) {
        setError('This subdomain is already taken. Please choose another one.');
        setIsLoading(false);
        return;
      }
      
      // Remove confirmPassword from data
      let phoneCountryCode = data.phoneCountryCode;
      if (phoneCountryCode && phoneCountryCode.includes('(')) {
        phoneCountryCode = phoneCountryCode.split(' ')[0];
      }
      const { confirmPassword, phoneAreaCode, phoneNumber, ...formData } = data;
      
      // Format phone information
      const fullPhoneNumber = `${phoneCountryCode}${phoneAreaCode ? ` (${phoneAreaCode})` : ''}${phoneNumber ? ` ${phoneNumber}` : ''}`.trim();
      
      // Format location
      const countryName = countries.find(c => c.code === data.country)?.name || data.country;
      const formattedLocation = `${data.city}${data.state ? `, ${data.state}` : ''}${countryName ? `, ${countryName}` : ''}`.trim();
      
      // Map form field names to match the service request structure
      const tenantRequest = {
        name: formData.name,
        subdomain: formData.subdomain,
        phone: {
          countryCode: phoneCountryCode,
          areaCode: phoneAreaCode,
          number: phoneNumber,
          full: fullPhoneNumber
        },
        location: {
          country: data.country,
          city: data.city,
          state: data.state,
          formatted: formattedLocation
        },
        industry: formData.industry,
        companySize: formData.companySize,
        requestedBy: {
          firstName: formData.ownerFirstName,
          lastName: formData.ownerLastName,
          email: formData.ownerEmail,
          password: formData.ownerPassword
        },
        status: 'requested'
      };
      
      localStorage.setItem('tenantRequest', JSON.stringify({
        ...tenantRequest,
        createdAt: new Date().toISOString(),
        ownerEmail: formData.ownerEmail
      }));
      
      console.log('Submitting tenant service request:', { 
        ...tenantRequest, 
        requestedBy: { ...tenantRequest.requestedBy, password: '****' } 
      });
      
      const response = await tenantRequestService.submitTenantRequest(tenantRequest);
      
      console.log('Tenant request submitted successfully:', response);
      
      // Send email notification to the user
      try {
        await emailService.sendEmail({
          to: formData.ownerEmail,
          subject: `Organization Registration Request - ${formData.name}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); padding: 20px; text-align: center; color: white;">
                <h1>Registration Request Received</h1>
              </div>
              <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
                <p>Hello ${formData.ownerFirstName},</p>
                <p>Thank you for submitting your registration request for <strong>${formData.name}</strong>.</p>
                <p>Your request is now pending approval from our administrators. Here's a summary of your request:</p>
                <ul>
                  <li><strong>Organization Name:</strong> ${formData.name}</li>
                  <li><strong>Subdomain:</strong> ${formData.subdomain}</li>
                  <li><strong>Admin Email:</strong> ${formData.ownerEmail}</li>
                  <li><strong>Industry:</strong> ${formData.industry}</li>
                  <li><strong>Company Size:</strong> ${formData.companySize}</li>
                </ul>
                <p>You will receive another email when your request has been reviewed. You can also check the status of your request at any time.</p>
                <div style="text-align: center; margin: 30px 0;">
                  <a href="${window.location.origin}/pending-approval" style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Check Request Status</a>
                </div>
                <p>If you have any questions, please contact our support team.</p>
                <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
                <p style="color: #666; font-size: 12px;">© ${new Date().getFullYear()} Transform Portal</p>
              </div>
            </div>
          `
        });
        console.log('Registration confirmation email sent successfully');
      } catch (emailError) {
        // Don't block the form submission if email sending fails
        console.error('Failed to send confirmation email:', emailError);
      }
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/pending-approval', { 
          state: { 
            requestData: {
              name: tenantRequest.name,
              subdomain: tenantRequest.subdomain,
              requestedBy: tenantRequest.requestedBy,
              phone: tenantRequest.phone,
              location: tenantRequest.location,
              industry: tenantRequest.industry,
              companySize: tenantRequest.companySize,
              createdAt: new Date().toISOString()
            } 
          } 
        });
      }, 1000);
    } catch (error) {
      console.error('Tenant request error:', error);
      if (error.response) {
        if (error.response.status === 409) {
          setError('An organization with this subdomain or email already exists in our request queue.');
        } else if (error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError(`Request submission failed (${error.response.status}). Please try again.`);
        }
      } else if (error.request) {
        setError('Could not connect to the server. Please check your internet connection and try again.');
      } else {
        setError('Request submission failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const steps = ['Organization Details', 'Admin Account', 'Company Information'];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" component="h2" fontWeight={600}>
                Tell us about your organization
              </Typography>
            </Box>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Organization name is required',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters'
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Organization Name"
                  variant="outlined"
                  error={!!errors.name}
                  helperText={errors.name?.message || ' '}
                  InputProps={{
                    sx: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', borderRadius: 2 }
                  }}
                  sx={{ mb: 3 }}
                  placeholder="Enter your organization's name"
                />
              )}
            />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Create your subdomain
            </Typography>
            <SubdomainInput>
              <Controller
                name="subdomain"
                control={control}
                rules={{
                  required: 'Subdomain is required',
                  minLength: {
                    value: 3,
                    message: 'Subdomain must be at least 3 characters'
                  },
                  pattern: {
                    value: /^[a-z0-9-]+$/,
                    message: 'Subdomain can only contain lowercase letters, numbers, and hyphens'
                  },
                  validate: value => subdomainAvailable !== false || 'This subdomain is already taken'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    placeholder="yourorganization"
                    onChange={(e) => handleSubdomainChange(e)}
                    error={!!errors.subdomain}
                    InputProps={{
                      sx: {
                        borderRadius: '4px',
                        bgcolor: subdomainAvailable === true ? 'rgba(76, 175, 80, 0.05)' : undefined
                      },
                      startAdornment: checkingSubdomain ? (
                        <InputAdornment position="start">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : subdomainAvailable === true ? (
                        <InputAdornment position="start">
                          <DomainVerificationIcon color="success" />
                        </InputAdornment>
                      ) : null
                    }}
                  />
                )}
              />
            </SubdomainInput>
            {errors.subdomain ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 1 }}>
                {errors.subdomain.message}
              </Typography>
            ) : checkingSubdomain ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 1 }}>
                Checking availability...
              </Typography>
            ) : subdomainAvailable === true ? (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, ml: 1 }}>
                This subdomain is available
              </Typography>
            ) : subdomainAvailable === false ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 1 }}>
                This subdomain is already taken
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 1 }}>
                Example: acme-inc, mycompany123
              </Typography>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                Phone Number
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name="phoneCountryCode"
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field: { onChange, value, ...rest } }) => (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Country Code</InputLabel>
                        <Select
                          value={value}
                          onChange={onChange}
                          {...rest}
                          label="Country Code"
                          error={!!errors.phoneCountryCode}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 300 },
                            },
                          }}
                        >
                          <MenuItem key="US" value="+1">
                            +1 (United States)
                          </MenuItem>
                          {countries
                            .filter(country => country.code !== 'US' && country.phoneCode !== '+1')
                            .map((country) => (
                              <MenuItem key={country.code} value={country.phoneCode}>
                                {country.phoneCode} ({country.name})
                              </MenuItem>
                            ))}
                          {countries
                            .filter(country => country.code !== 'US' && country.phoneCode === '+1')
                            .map((country) => (
                              <MenuItem key={country.code} value={`${country.phoneCode} (${country.name})`}>
                                {country.phoneCode} ({country.name})
                              </MenuItem>
                            ))}
                        </Select>
                        {errors.phoneCountryCode && (
                          <FormHelperText error>{errors.phoneCountryCode.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name="phoneAreaCode"
                    control={control}
                    rules={{ 
                      required: 'Required',
                      pattern: {
                        value: /^\d{3,4}$/,
                        message: 'Valid area code required'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Area Code"
                        placeholder="123"
                        variant="outlined"
                        error={!!errors.phoneAreaCode}
                        helperText={errors.phoneAreaCode?.message || ' '}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    rules={{ 
                      required: 'Required',
                      pattern: {
                        value: /^\d{7,10}$/,
                        message: 'Valid phone number required'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Phone Number"
                        placeholder="1234567"
                        variant="outlined"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message || ' '}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: 'Country is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Country</InputLabel>
                        <Select
                          {...field}
                          label="Country"
                          error={!!errors.country}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.country && (
                          <FormHelperText error>{errors.country.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="city"
                    control={control}
                    rules={{ required: 'City is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="City"
                        variant="outlined"
                        error={!!errors.city}
                        helperText={errors.city?.message || ' '}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: 'State/Province is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="State/Province"
                        variant="outlined"
                        error={!!errors.state}
                        helperText={errors.state?.message || ' '}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        );

      case 1:
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AdminPanelSettingsIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" component="h2" fontWeight={600}>
                Create your admin account
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              As the first user of your organization, you will be assigned the administrator role automatically.
              Your account will be set to pending status until approved by a system administrator.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="ownerFirstName"
                  control={control}
                  rules={{
                    required: 'First name is required',
                    minLength: {
                      value: 2,
                      message: 'First name must be at least 2 characters'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      error={!!errors.ownerFirstName}
                      helperText={errors.ownerFirstName?.message || ' '}
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="ownerLastName"
                  control={control}
                  rules={{
                    required: 'Last name is required',
                    minLength: {
                      value: 2,
                      message: 'Last name must be at least 2 characters'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Last Name"
                      variant="outlined"
                      error={!!errors.ownerLastName}
                      helperText={errors.ownerLastName?.message || ' '}
                      InputProps={{
                        sx: { borderRadius: 2 }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Controller
              name="ownerEmail"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  type="email"
                  margin="normal"
                  error={!!errors.ownerEmail}
                  helperText={errors.ownerEmail?.message || ' '}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              )}
            />
            
            <Box sx={{ mt: 3 }}>
              <Controller
                name="ownerPassword"
                control={control}
                rules={{
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message: 'Password must include uppercase, lowercase, number and special character'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Password"
                    variant="outlined"
                    type="password"
                    error={!!errors.ownerPassword}
                    helperText={errors.ownerPassword?.message || ' '}
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                )}
              />
              
              {password && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Password strength:</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'medium', 
                        color: `${passwordStrength.color}.main`
                      }}
                    >
                      {passwordStrength.label}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.score}
                    color={passwordStrength.color}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    For best security, use a mix of letters, numbers and special characters
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: 'Please confirm your password',
                validate: value => value === password || 'Passwords do not match'
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Confirm Password"
                  variant="outlined"
                  type="password"
                  margin="normal"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message || ' '}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              )}
            />
          </>
        );
        
      case 2:
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" component="h2" fontWeight={600}>
                Company Information
              </Typography>
            </Box>
            
            <Box sx={{ my: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CategoryIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle1">Industry</Typography>
              </Box>
              
              <Controller
                name="industry"
                control={control}
                rules={{ required: 'Please select your industry' }}
                render={({ field }) => (
                  <FormControl fullWidth variant="outlined" error={!!errors.industry}>
                    <InputLabel>Select Industry</InputLabel>
                    <Select
                      {...field}
                      label="Select Industry"
                    >
                      {industries.map((industry) => (
                        <MenuItem key={industry} value={industry}>
                          {industry}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.industry && (
                      <FormHelperText error>{errors.industry.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>
            
            <Box sx={{ my: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle1">Company Size</Typography>
              </Box>
              
              <Controller
                name="companySize"
                control={control}
                rules={{ required: 'Please select your company size' }}
                render={({ field }) => (
                  <FormControl fullWidth variant="outlined" error={!!errors.companySize}>
                    <InputLabel>Select Company Size</InputLabel>
                    <Select
                      {...field}
                      label="Select Company Size"
                    >
                      {companySizes.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.companySize && (
                      <FormHelperText error>{errors.companySize.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>
            
            <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2, border: '1px solid rgba(25, 118, 210, 0.1)' }}>
              <Typography variant="body2" color="text.secondary">
                This information helps us customize your experience and provide the most appropriate services for your organization.
              </Typography>
            </Box>
          </>
        );
        
      default:
        return 'Unknown step';
    }
  };

  if (success) {
    return (
      <Box 
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, rgba(10, 20, 41, 0.92), rgba(26, 42, 77, 0.95))',
          backgroundImage: `url(${backgroundImages[currentImageIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          padding: { xs: 2, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-image 2s ease-in-out',
        }}
      >
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 4,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '8px',
                background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 50%, #81c784 100%)',
                backgroundSize: '200% auto',
                animation: 'gradient 3s linear infinite',
                '@keyframes gradient': { 
                  '0%': { backgroundPosition: '0% center' },
                  '100%': { backgroundPosition: '200% center' }
                } 
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '250px',
                height: '250px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(76, 175, 80, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
                zIndex: 0
              }}
            />
            <Avatar
              sx={{
                bgcolor: '#4caf50',
                width: 72,
                height: 72,
                margin: '0 auto 24px',
                boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)',
                position: 'relative',
                zIndex: 1
              }}
            >
              <CheckCircleIcon fontSize="large" />
            </Avatar>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#2c3e50', 
                mb: 2,
                position: 'relative',
                zIndex: 1
              }}
            >
              Request Submitted!
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                position: 'relative',
                zIndex: 1,
                fontSize: '1.1rem'
              }}
            >
              Your organization registration request has been submitted successfully. 
              Our team will review your application and get back to you shortly.
            </Typography>
            <Button
              component={Link}
              to="/pending-approval"
              variant="contained"
              sx={{
                background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 50%, #81c784 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #388e3c 0%, #4caf50 50%, #66bb6a 100%)',
                  boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)'
                },
                py: 1.5,
                px: 4,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                position: 'relative',
                zIndex: 1
              }}
            >
              View Request Status
            </Button>
          </Paper>
        </Fade>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(10, 20, 41, 0.92), rgba(26, 42, 77, 0.95))',
        backgroundImage: `url(${backgroundImages[currentImageIndex]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay',
        padding: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-image 2s ease-in-out',
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 5 } }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            fontWeight="700" 
            color="white"
            sx={{ 
              textShadow: '0 2px 10px rgba(0,0,0,0.3)',
              mb: 1, 
            }}
          >
            Register your organization
          </Typography>
          <Typography 
            variant="h6" 
            color="rgba(255,255,255,0.9)" 
            sx={{
              fontWeight: 400,
              textShadow: '0 2px 8px rgba(0,0,0,0.2)', 
            }}
          >
            Create your own branded membership portal
          </Typography>
        </Box>
        <Fade in={true} timeout={800}>
          <GradientPaper elevation={24}>
            <GradientDivider />
            <Box sx={{ p: { xs: 3, md: 5 } }}>
              {error && ( 
                <Alert 
                  severity="error" 
                  variant="filled"
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2, 
                    boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                  }}
                >
                  {error}
                </Alert>
              )}
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel={!isMobile} 
                orientation={isMobile ? "vertical" : "horizontal"}
                sx={{ mb: 4 }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <form onSubmit={handleSubmit(onSubmit)}>
                {getStepContent(activeStep)}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{ 
                      borderRadius: theme.shape.borderRadius * 2,
                      textTransform: 'none',
                      px: 3
                    }}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <StyledButton
                      type="submit"
                      disabled={isLoading}
                      endIcon={isLoading && <CircularProgress size={20} color="inherit" />}
                    >
                      {isLoading ? 'Registering...' : 'Complete Registration'}
                    </StyledButton>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{
                        borderRadius: theme.shape.borderRadius * 2,
                        textTransform: 'none',
                        px: 3,
                        background: 'linear-gradient(90deg, #0062cc 0%, #0073e6 50%, #2196f3 100%)',
                        '&:hover': {
                          backgroundPosition: 'right center',
                        },
                      }}
                    >
                      Continue
                    </Button>
                  )}
                </Box>
              </form>
            </Box>
            <Box 
              sx={{ 
                bgcolor: 'rgba(25, 118, 210, 0.04)', 
                p: 3, 
                borderTop: '1px solid rgba(0,0,0,0.06)', 
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Already have an organization?{' '}
                <Link 
                  to="/login" 
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: 600, 
                    textDecoration: 'none'
                  }}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
          </GradientPaper>
        </Fade>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography 
            variant="body2" 
            color="rgba(255,255,255,0.7)"
            sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
          >
            © {new Date().getFullYear()} Your Enterprise Portal. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default RegisterTenantPage;